/**
 * FetchExclusiveContentSubscribersPageUseCase
 *
 * Loads one page of the current user's subscribers (users subscribed to me).
 * Used by the Subscribers page.
 *
 * Flutter parity + web defence-in-depth:
 *   - lib/presentation/screens/subscriptions/subscribers_screen.dart:75-110
 *     - Fetches with `meSubscriber = false`.
 *     - Page size: AppConstants.subscriptionsPagination (= 9, which matches
 *       SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT on web).
 *   - Flutter does no client-side filtering. The server handles it: the
 *     `blockUser` cloud function inactivates both sides of the affected
 *     subscription documents (block_user.ts:81-97) and the query filters by
 *     `status in [active, cancelled]`. On web we add a small in-memory
 *     filter against `currentUser.blockedUsers` / `usersBlockedMe` as
 *     defence-in-depth for the race window before that cleanup lands locally.
 *
 * Errors from the service propagate — the hook layer logs via
 * appLogger.error.
 */

import type { PageCursor } from '@/data/types/json';

import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import {
  exclusiveContentSubscribersService,
  type ExclusiveContentSubscribersService,
} from '@/domain/services/exclusiveContentSubscription/exclusiveContentSubscribersService';
import { shouldFilterOutBlockedUserUseCase } from '@/domain/usecases/user/shouldFilterOutBlockedUserUseCase';

export interface FetchExclusiveContentSubscribersPageParams {
  currentUser: UserEntity | null;
  pageSize?: number;
  cursor?: PageCursor;
}

export interface FetchExclusiveContentSubscribersPageResult {
  subscribers: ExclusiveContentSubscriptionEntity[];
  cursor?: PageCursor;
  hasMore: boolean;
}

export class FetchExclusiveContentSubscribersPageUseCase {
  private readonly subscribersService: ExclusiveContentSubscribersService;

  constructor(subscribersService?: ExclusiveContentSubscribersService) {
    this.subscribersService = subscribersService ?? exclusiveContentSubscribersService;
  }

  async execute(
    params: FetchExclusiveContentSubscribersPageParams,
  ): Promise<FetchExclusiveContentSubscribersPageResult> {
    const { currentUser, pageSize = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT, cursor } = params;

    const currentUserId = currentUser?.firebaseId;
    if (!currentUserId) {
      return { subscribers: [], cursor: undefined, hasMore: false };
    }

    const page = await this.subscribersService.fetchSubscribersPage({
      currentUserId,
      pageSize,
      cursor,
    });

    // `userId` on a subscribers-page row is the OTHER user — the subscriber.
    // Matches Flutter subscribers_screen.dart:92 which uses
    // `subscription.userId` as the row's user identity.
    const filtered = page.subscribers.filter(
      (subscription) => !shouldFilterOutBlockedUserUseCase.execute(subscription.userId, currentUser),
    );

    return {
      subscribers: filtered,
      cursor: page.cursor,
      hasMore: page.hasMore,
    };
  }
}

export const fetchExclusiveContentSubscribersPageUseCase =
  new FetchExclusiveContentSubscribersPageUseCase();
